import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';

import {
  Briefcase,
  Buildings,
  CheckCircle,
  CircleNotch,
  Clock,
  Eye,
  Funnel,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  Sparkle,
  Target,
  Trash,
  User,
  WarningCircle
} from '@phosphor-icons/react';
const JobDescriptions = () => {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  
  const [jobDescriptions, setJobDescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingJD, setEditingJD] = useState(null);
  const [modalData, setModalData] = useState({
    title: '',
    department: '',
    seniority: '',
    employment_type: '',
    raw_text: ''
  });
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState(null);
  
  // Analyze state
  const [analyzingId, setAnalyzingId] = useState(null);

  useEffect(() => {
    fetchJobDescriptions();
  }, [activeOrganization]);

  const fetchJobDescriptions = async () => {
    try {
      setLoading(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/job-descriptions`,
        { method: 'GET' },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setJobDescriptions(data.job_descriptions || []);
      }
    } catch (err) {
      console.error('Error fetching job descriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingJD(null);
    setModalData({
      title: '',
      department: '',
      seniority: '',
      employment_type: '',
      raw_text: ''
    });
    setModalError(null);
    setShowModal(true);
  };

  const handleEdit = (jd) => {
    setEditingJD(jd);
    setModalData({
      title: jd.title,
      department: jd.department || '',
      seniority: jd.seniority || '',
      employment_type: jd.employment_type || '',
      raw_text: jd.raw_text
    });
    setModalError(null);
    setShowModal(true);
  };

  const handleSave = async (andAnalyze = false) => {
    try {
      setSaving(true);
      setModalError(null);

      // Validation
      if (!modalData.title.trim()) {
        setModalError('Job title is required');
        return;
      }
      if (!modalData.raw_text.trim()) {
        setModalError('Job description text is required');
        return;
      }

      const url = editingJD
        ? `${BACKEND_URL}/api/job-descriptions/${editingJD.id}`
        : `${BACKEND_URL}/api/job-descriptions`;

      const method = editingJD ? 'PUT' : 'POST';

      const response = await fetchWithOrg(
        url,
        {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(modalData)
        },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        await fetchJobDescriptions();
        setShowModal(false);

        // If "Save & Analyze" was clicked, trigger analysis
        if (andAnalyze && data.job_description) {
          handleAnalyze(data.job_description.id);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setModalError(errorData.detail || 'Failed to save job description');
      }
    } catch (err) {
      console.error('Error saving job description:', err);
      setModalError('Failed to save job description. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAnalyze = async (jdId) => {
    try {
      setAnalyzingId(jdId);

      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/job-descriptions/${jdId}/analyze`,
        { method: 'POST' },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        await fetchJobDescriptions();
        
        // Show success message
        alert(`✅ Analysis complete! ${data.opportunities_created} opportunities identified.`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`❌ Analysis failed: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error analyzing job description:', err);
      alert('❌ Failed to analyze job description. Please try again.');
    } finally {
      setAnalyzingId(null);
    }
  };

  const handleDelete = async (jdId) => {
    if (!window.confirm('Are you sure you want to delete this job description?')) {
      return;
    }

    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/job-descriptions/${jdId}`,
        { method: 'DELETE' },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        await fetchJobDescriptions();
      }
    } catch (err) {
      console.error('Error deleting job description:', err);
      alert('Failed to delete job description');
    }
  };

  const handleViewOpportunities = (jd) => {
    // Navigate to opportunities page with filter
    const params = new URLSearchParams({
      source: 'job_description',
      jd_id: jd.id,
      jd_title: jd.title
    });
    navigate(`/opportunities?${params.toString()}`);
  };

  // Funnel job descriptions
  const filteredJDs = jobDescriptions.filter(jd => {
    const matchesSearch = !searchQuery || 
      jd.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (jd.department && jd.department.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'analyzed' && jd.analyzed) ||
      (filterStatus === 'not_analyzed' && !jd.analyzed);
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (jd) => {
    if (jd.analyzed) {
      return (
        <span className="px-3 py-1 rounded-full bg-[rgba(16,185,129,0.2)] text-[#10B981] text-xs font-semibold flex items-center gap-1">
          <CheckCircle size={14} />
          Analyzed
        </span>
      );
    }
    return (
      <span className="px-3 py-1 rounded-full bg-[rgba(245,158,11,0.2)] text-[#F59E0B] text-xs font-semibold flex items-center gap-1">
        <Clock size={14} />
        Not Analyzed
      </span>
    );
  };

  return (
      <>
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-1 flex items-center gap-3">
            <Briefcase size={24} className="text-[#00E5C0]" />
            Job Descriptions
          </h1>
          <p className="text-sm text-[#B0B8C4]">
            Paste or upload job descriptions to automatically identify AI automation opportunities for each role.
          </p>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {/* MagnifyingGlass */}
          <div className="flex-1 relative">
            <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search job descriptions..."
              className="w-full pl-12 pr-4 py-3 rounded-xl glass text-white placeholder-white/40 border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] focus:outline-none"
            />
          </div>

          {/* Funnel */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-3 rounded-xl glass text-white border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="analyzed">Analyzed</option>
            <option value="not_analyzed">Not Analyzed</option>
          </select>

          {/* Add Button */}
          <button
            onClick={handleAddNew}
            className="px-6 py-3 rounded-xl bg-[#6366F1] hover:bg-[#5558E6] text-white font-bold flex items-center gap-2 transition-colors whitespace-nowrap"
          >
            <Plus size={20} />
            Add Job Description
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="glass rounded-xl p-4">
            <p className="text-[#A0AEC0] text-sm mb-1">Total Job Descriptions</p>
            <p className="text-white text-2xl font-bold">{jobDescriptions.length}</p>
          </div>
          <div className="glass rounded-xl p-4">
            <p className="text-[#A0AEC0] text-sm mb-1">Analyzed</p>
            <p className="text-[#10B981] text-2xl font-bold">
              {jobDescriptions.filter(jd => jd.analyzed).length}
            </p>
          </div>
          <div className="glass rounded-xl p-4">
            <p className="text-[#A0AEC0] text-sm mb-1">Total Opportunities</p>
            <p className="text-[#6366F1] text-2xl font-bold">
              {jobDescriptions.reduce((sum, jd) => sum + jd.num_opportunities_created, 0)}
            </p>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="glass rounded-2xl p-12 text-center">
            <CircleNotch size={48} className="animate-spin text-[#6366F1] mx-auto mb-4" />
            <p className="text-white">Loading job descriptions...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && jobDescriptions.length === 0 && (
          <div className="glass rounded-2xl p-12 text-center">
            <Briefcase size={64} className="text-[#6366F1] mx-auto mb-4 opacity-50" />
            <h3 className="text-white text-xl font-bold mb-2">No Job Descriptions Yet</h3>
            <p className="text-[#A0AEC0] mb-6">
              Add your first job description to start identifying AI automation opportunities.
            </p>
            <button
              onClick={handleAddNew}
              className="px-6 py-3 rounded-xl bg-[#6366F1] hover:bg-[#5558E6] text-white font-bold inline-flex items-center gap-2 transition-colors"
            >
              <Plus size={20} />
              Add Job Description
            </button>
          </div>
        )}

        {/* Job Descriptions List */}
        {!loading && filteredJDs.length > 0 && (
          <div className="space-y-4">
            {filteredJDs.map((jd) => (
              <motion.div
                key={jd.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass rounded-xl p-6 hover:bg-[rgba(255,255,255,0.05)] transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-white text-xl font-bold">{jd.title}</h3>
                      {getStatusBadge(jd)}
                    </div>
                    
                    <div className="flex items-center gap-6 text-sm text-[#A0AEC0] mb-3">
                      {jd.department && (
                        <div className="flex items-center gap-2">
                          <Buildings size={16} />
                          {jd.department}
                        </div>
                      )}
                      {jd.seniority && (
                        <div className="flex items-center gap-2">
                          <User size={16} />
                          {jd.seniority}
                        </div>
                      )}
                      {jd.analyzed && (
                        <div className="flex items-center gap-2 text-[#6366F1]">
                          <Target size={16} />
                          {jd.num_opportunities_created} opportunities
                        </div>
                      )}
                    </div>

                    <p className="text-[#A0AEC0] text-sm line-clamp-2">
                      {jd.raw_text.substring(0, 200)}...
                    </p>
                  </div>

                  <div className="flex flex-col gap-2">
                    {!jd.analyzed ? (
                      <button
                        onClick={() => handleAnalyze(jd.id)}
                        disabled={analyzingId === jd.id}
                        className="px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#5558E6] text-white font-semibold flex items-center gap-2 transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        {analyzingId === jd.id ? (
                          <>
                            <CircleNotch size={16} className="animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Sparkle size={16} />
                            Analyze for AI
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleViewOpportunities(jd)}
                        className="px-4 py-2 rounded-lg glass text-white font-semibold flex items-center gap-2 hover:bg-[rgba(255,255,255,0.1)] transition-colors whitespace-nowrap"
                      >
                        <Eye size={16} />
                        View Opportunities
                      </button>
                    )}

                    <button
                      onClick={() => handleEdit(jd)}
                      className="px-4 py-2 rounded-lg glass text-white font-semibold flex items-center gap-2 hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                    >
                      <PencilSimple size={16} />
                      Edit
                    </button>

                    <button
                      onClick={() => handleDelete(jd.id)}
                      className="px-4 py-2 rounded-lg glass text-[#EF4444] font-semibold flex items-center gap-2 hover:bg-red-500/20 transition-colors"
                    >
                      <Trash size={16} />
                      Trash
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* No Results */}
        {!loading && jobDescriptions.length > 0 && filteredJDs.length === 0 && (
          <div className="glass rounded-2xl p-12 text-center">
            <WarningCircle size={48} className="text-[#F59E0B] mx-auto mb-4" />
            <p className="text-white text-lg">No job descriptions match your filters</p>
          </div>
        )}
      </div>

      {/* Add/PencilSimple Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[rgba(0,0,0,0.8)]"
          onClick={() => setShowModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h2 className="text-2xl font-bold text-white mb-6">
                {editingJD ? 'Edit Job Description' : 'Add Job Description'}
              </h2>

              {modalError && (
                <div className="mb-4 p-4 rounded-xl bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] flex items-center gap-3">
                  <WarningCircle size={20} className="text-[#EF4444]" />
                  <p className="text-[#EF4444] text-sm">{modalError}</p>
                </div>
              )}

              <div className="space-y-4">
                {/* Job Title */}
                <div>
                  <label className="block text-white font-semibold mb-2">
                    Job Title <span className="text-[#EF4444]">*</span>
                  </label>
                  <input
                    type="text"
                    value={modalData.title}
                    onChange={(e) => setModalData({ ...modalData, title: e.target.value })}
                    placeholder="e.g., Senior Sales Manager"
                    className="w-full px-4 py-3 rounded-xl glass text-white placeholder-white/40 border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] focus:outline-none"
                  />
                </div>

                {/* Department and Seniority */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white font-semibold mb-2">Department / Team</label>
                    <input
                      type="text"
                      value={modalData.department}
                      onChange={(e) => setModalData({ ...modalData, department: e.target.value })}
                      placeholder="e.g., Sales"
                      className="w-full px-4 py-3 rounded-xl glass text-white placeholder-white/40 border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-white font-semibold mb-2">Seniority</label>
                    <input
                      type="text"
                      value={modalData.seniority}
                      onChange={(e) => setModalData({ ...modalData, seniority: e.target.value })}
                      placeholder="e.g., Senior, Junior"
                      className="w-full px-4 py-3 rounded-xl glass text-white placeholder-white/40 border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] focus:outline-none"
                    />
                  </div>
                </div>

                {/* Job Description Text */}
                <div>
                  <label className="block text-white font-semibold mb-2">
                    Job Description <span className="text-[#EF4444]">*</span>
                  </label>
                  <textarea
                    value={modalData.raw_text}
                    onChange={(e) => setModalData({ ...modalData, raw_text: e.target.value })}
                    placeholder="Paste the full job description here..."
                    rows={12}
                    className="w-full px-4 py-3 rounded-xl glass text-white placeholder-white/40 border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] focus:outline-none resize-none"
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                  className="px-6 py-3 rounded-xl glass text-white font-semibold hover:bg-[rgba(255,255,255,0.05)] transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSave(false)}
                  disabled={saving}
                  className="flex-1 px-6 py-3 rounded-xl glass text-white font-semibold hover:bg-[rgba(255,255,255,0.1)] transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => handleSave(true)}
                  disabled={saving}
                  className="flex-1 px-6 py-3 rounded-xl bg-[#6366F1] hover:bg-[#5558E6] text-white font-semibold transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save & Analyze'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
      </>
  );
};

export default JobDescriptions;
